import { apiFetch, bootstrapSession } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { AppSettings } from "@/api/types";
import { analytics } from "@/analytics";
import { deferredLinkPath, takeDeferredLink } from "@/analytics/launch";
import { platform } from "@/platform";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import { abandonOnboarding } from "./onboarding";
import { runOtaUpdate } from "./otaUpdate";

export type BootstrapResult = { next: string };

/**
 * 1.0 ③: ⓪ OTA 새 번들이면 스플래시에서 받아 적용(앱, AC0-170) ① app/settings → configStore(실패 시 persist 캐시, 캐시도 없으면 throw)
 * ② minBuild·maintenance → /blocked ③ 세션 복원(앱: refresh 선행 / 웹: GET /me 선행) ④ 딥링크(Android 설치 뒤 첫 부팅은 Play 리퍼러의 plot, AC0-360) 또는 /home.
 * 빌드 번호는 platform.app.getInfo()(네이티브 App.getInfo, 웹은 null → 보내지 않음).
 */
export async function bootstrap(): Promise<BootstrapResult> {
  await runOtaUpdate();
  // Amplitude: OTA 다음(새 번들이면 이미 다시 로드됐다), 설정 요청 전. bundle_version 을 먼저 정해 공통 속성에 싣는다. 실패해도 부팅은 계속.
  const bundle = await platform.updater.currentBundle().catch(() => null);
  if (bundle) analytics.setBundleVersion(bundle);
  void analytics.init();
  const config = useConfigStore.getState();
  const { build } = await platform.app.getInfo().catch(() => ({ version: "", build: null }));
  let settings: AppSettings | null = null;
  try {
    settings = await apiFetch<AppSettings>(ep.appSettings({ platform: platform.clientKind, ...(build !== null ? { build } : {}) }), {
      noAuth: true,
      noRefresh: true,
    });
    config.setSettings(settings);
  } catch {
    settings = config.settings;
    if (!settings) throw new Error("settings unavailable");
  }

  if (build !== null && build < settings.minBuild) return { next: "/blocked?reason=update" };
  if (settings.maintenance?.active) return { next: "/blocked?reason=maintenance" };

  await bootstrapSession();
  // 1b(AC0-149): 온보딩을 마치지 않은 세션이 남아 있으면 이탈로 보고 비회원으로 시작한다
  await abandonOnboarding();

  const deepLink = useUiStore.getState().pendingDeepLink;
  if (deepLink) {
    useUiStore.getState().setPendingDeepLink(null);
    return { next: `/plots/${encodeURIComponent(deepLink.plot)}` };
  }
  const deferred = await deferredDeepLink();
  return { next: deferred ?? "/home" };
}

/**
 * Android 디퍼드 딥링크(AC0-360): 설치 뒤 첫 부팅 한 번, Play Install Referrer 의 `plot` 으로 간다.
 * 리퍼러 조회가 일시 실패(reject)하면 표식을 남기지 않아 다음 부팅에 다시 본다. 플러그인이 첫 성공 값을 저장하므로 두 번째 부팅부터는 즉시 돌아온다.
 */
async function deferredDeepLink(): Promise<string | null> {
  if (platform.clientKind !== "android") return null;
  let referrer: string | null;
  try {
    referrer = await platform.analytics.getInstallReferrer();
  } catch {
    return null;
  }
  const path = deferredLinkPath(referrer);
  if (!path) return null;
  return takeDeferredLink() ? path : null;
}

/**
 * 콜드 스타트 딥링크(부팅이 정한 /home·/blocked 외 경로)는 홈을 먼저 깔고 그 위에 push 한다(뒤로 = 홈, AC0-88·AC0-360).
 * 루트 index loader 가 맡겨 두면 앱 셸이 마운트 직후 꺼내 이동한다.
 */
let coldStartPath: string | null = null;
export function deferColdStartPath(path: string): void {
  coldStartPath = path;
}
export function takeColdStartPath(): string | null {
  const p = coldStartPath;
  coldStartPath = null;
  return p;
}

let booted: Promise<BootstrapResult> | null = null;

/** 앱 수명 동안 한 번. 실패하면 다음 호출이 다시 시도한다(부트스트랩 실패 화면 "다시 시도"). */
export function ensureBootstrapped(): Promise<BootstrapResult> {
  booted ??= bootstrap().catch((e) => {
    booted = null;
    throw e;
  });
  return booted;
}

export function resetBootstrap(): void {
  booted = null;
}
